'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { Box, Container } from '@mui/material'
import { Toaster } from 'react-hot-toast'

import { getBrandName } from '@/src/utils/buildUtils'
import { useWallet, useWalletContentData } from '@/src/hooks/wallet'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import { WalletInfoResponse, WalletTransactionsResponse } from '@/src/types/wallet'
import { FAQResponse } from '@/src/api/rtkQuery/walletFAQApi'

import WalletDesktopFlyinContent from './WalletDesktopFlyinContent'
import WalletDesktopCleartripContent from './WalletDesktopCleartripContent'
import { WalletFAQ } from '../../mobile/wallet/walletFAQ'

// Dynamically load SignInModal
const SignInModalWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "signin-modal" */ '@/src/components/common/header/variants/flyin/SignInModal/SignInModalWrapper'
        ),
    { ssr: false }
)

interface WalletDesktopPageWrapperProps {
    initialWalletInfo?: WalletInfoResponse | null
    initialTransactions?: WalletTransactionsResponse | null
    initialFAQ?: FAQResponse | null
    domainCountry?: string
}

const WalletDesktopPageWrapper: React.FC<WalletDesktopPageWrapperProps> = ({
    initialWalletInfo,
    initialTransactions,
    initialFAQ,
    domainCountry
}) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('wallet')
    const tVariants = useTranslations('variants')
    const tCommon = useTranslations('common')
    const aboutWalletLabel = t('aboutWallet', { brandName: tVariants(getBrandName()) })

    const [modalOpen, setModalOpen] = useState(false)
    const [tooltipOpen, setTooltipOpen] = useState(false)

    // Use wallet hook with initial SSR data
    const wallet = useWallet({
        initialWalletInfo,
        initialTransactions
    })

    const isRtl = locale === 'ar'

    const {
        formattedTransactions,
        cleartripFormattedTransactions,
        translatedBalanceError,
        translatedTransactionsError,
        formatAmount,
        cleartripTransactionLabels
    } = useWalletContentData({ wallet, locale })

    // Handler for FAQ section
    const handleOpenFAQ = useCallback(() => {
        // Scroll to FAQ section (which is rendered below)
        if (typeof window !== 'undefined') {
            const faqElement = document.getElementById('wallet-faq-section')
            if (faqElement) {
                faqElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        }
    }, [])

    // Show sign-in modal if not logged in
    useEffect(() => {
        if (typeof window !== 'undefined' && !wallet.isLoggedIn) {
            setModalOpen(true)
        }
    }, [wallet.isLoggedIn])

    // Handlers
    const handleModalClose = useCallback(() => {
        setModalOpen(false)
        // Redirect if still not logged in
        if (!wallet.isLoggedIn) {
            router.push(`/${locale}`)
        }
    }, [wallet.isLoggedIn, router, locale])

    const handleTooltipOpen = useCallback(() => {
        setTooltipOpen(true)
    }, [])

    const handleTooltipClose = useCallback(() => {
        setTooltipOpen(false)
    }, [])

    const baseWalletPageProps = useMemo(() => ({
        displayBalance: wallet.displayBalance,
        displayCurrency: wallet.displayCurrency,
        domainBalances: wallet.domainBalances,
        hasMultipleDomainBalances: wallet.hasMultipleDomainBalances,
        hasMoreTransactions: wallet.hasMoreTransactions,
        activeTab: wallet.activeTab,
        onTabChange: wallet.setActiveTab,
        isLoadingBalance: wallet.isLoadingBalance,
        isFetchingMore: wallet.isFetchingTransactions,
        balanceError: translatedBalanceError,
        transactionsError: translatedTransactionsError,
        onLoadMore: wallet.loadMoreTransactions,
        onRefresh: wallet.refreshWallet,
        isTooltipOpen: tooltipOpen,
        onTooltipOpen: handleTooltipOpen,
        onTooltipClose: handleTooltipClose,
        onOpenFAQ: handleOpenFAQ,
        formatAmount,
        t,
        tCommon,
        locale,
        isRtl,
        aboutWalletLabel
    }), [
        wallet.displayBalance,
        wallet.displayCurrency,
        wallet.domainBalances,
        wallet.hasMultipleDomainBalances,
        wallet.hasMoreTransactions,
        wallet.activeTab,
        wallet.setActiveTab,
        wallet.isLoadingBalance,
        wallet.isFetchingTransactions,
        translatedBalanceError,
        translatedTransactionsError,
        wallet.loadMoreTransactions,
        wallet.refreshWallet,
        tooltipOpen,
        handleTooltipOpen,
        handleTooltipClose,
        handleOpenFAQ,
        formatAmount,
        t,
        tCommon,
        locale,
        isRtl,
        aboutWalletLabel
    ])

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'white'
            }}
        >
            <Toaster position="bottom-right" reverseOrder={false} gutter={8} />
            {/* Desktop Header */}
            <HeaderContainer domainCountry={domainCountry} />

            {/* Main Content */}
            <Box sx={{ flex: 1, bgcolor: 'white' }}>
                <Container maxWidth="lg">
                    {wallet.isCleartrip ? (
                        <WalletDesktopCleartripContent
                            walletPageProps={{
                                ...baseWalletPageProps,
                                formattedTransactions: [],
                                isLoadingTransactions: false
                            }}
                            transactions={cleartripFormattedTransactions}
                            hasMoreTransactions={wallet.hasMoreTransactions}
                            isFetchingMore={wallet.isFetchingTransactions}
                            onLoadMore={wallet.loadMoreTransactions}
                            formatAmount={formatAmount}
                            {...cleartripTransactionLabels}
                        />
                    ) : (
                        <WalletDesktopFlyinContent
                            {...baseWalletPageProps}
                            formattedTransactions={formattedTransactions}
                            isLoadingTransactions={wallet.isLoadingTransactions}
                        />
                    )}
                </Container>

                {/* FAQ Section */}
                <Container maxWidth="lg" id="wallet-faq-section">
                    <WalletFAQ initialFAQ={initialFAQ} isDesktop />
                </Container>
            </Box>

            {/* Sign In Modal */}
            {modalOpen && <SignInModalWrapper open={modalOpen} onClose={handleModalClose} />}
        </Box>
    )
}

export { WalletDesktopPageWrapper }
export default WalletDesktopPageWrapper
